import React, { useCallback, useMemo } from 'react';
import { View } from 'react-native';
import moment from 'moment';

import { STATUS } from '../../../common/constants';
import { formatCurrency } from '../../../common/utils';
import { valueExists } from '../../../common/utils/utils';
import Text from '../../components/text/Text';
import { ListItem } from '../../components/list/ListItem';
import { StatusBadge, Tag } from '../../design/components';
import { useThemedStyles } from '../../hooks/useThemedStyles';
import { getStatusLabel } from '../../utils/statusLabel';

const StocktakeListItem = ({ item, customProps }) => {
	const styles = useThemedStyles(createStyles);
	const { onPressItem } = customProps || {};

	const handlePress = useCallback(() => {
		if (onPressItem) {
			onPressItem(item);
		}
	}, [item, onPressItem]);

	const formattedDate = useMemo(() => {
		const sourceDate = item.date || item.created;
		return sourceDate ? moment(sourceDate).format('DD MMM YYYY') : 'No date';
	}, [item.created, item.date]);

	const differenceLabel = useMemo(() => {
		if (!valueExists(item.valueOfDifference)) {
			return '—';
		}
		return formatCurrency(item.valueOfDifference, false);
	}, [item.valueOfDifference]);

	const percentageDifference = valueExists(item.percentageDifference)
		? `${item.percentageDifference}%`
		: '—';

	const statusLabel = getStatusLabel(item.status, 'Pending');

	return (
		<ListItem
			onPress={handlePress}
			style={styles.item}
		>
			<View style={styles.header}>
				<Text style={styles.title}>
					Stocktake {formattedDate}
				</Text>
				<StatusBadge
					label={statusLabel}
					tone={item.status === STATUS.COMPLETE ? 'success' : 'neutral'}
					size="small"
				/>
			</View>
			<Text style={styles.meta}>
				Warehouse: {item.warehouse?.name || 'Unassigned'}
			</Text>
			<Text style={styles.meta}>
				Person: {
					item.user?.destroyed
						? 'User removed'
						: item.user
							? `${item.user.firstname || ''} ${item.user.lastname || ''}`.trim() || 'Unnamed'
							: 'Not assigned'
				}
			</Text>
			<View style={styles.row}>
				<Text style={styles.meta}>
					Value difference: {differenceLabel}
				</Text>
				<Text style={styles.meta}>
					Variance: {percentageDifference}
				</Text>
			</View>
			{item.valueNotAccurate && (
				<Tag label="Value may be inaccurate" tone="warning" style={styles.warning} />
			)}
		</ListItem>
	);
};

const createStyles = ({ ui: { colors, type, space } }) => ({
	item: {
		marginHorizontal: space.gutter,
		marginTop: 0,
		marginBottom: space.sm,
		paddingHorizontal: 14,
		paddingVertical: space.md,
		gap: space.xxs,
	},
	header: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'space-between',
		gap: space.md,
		marginBottom: space.xs,
	},
	title: {
		...type.rowTitleSemibold,
		color: colors.text,
		flexShrink: 1,
	},
	meta: {
		...type.secondary,
		color: colors.textSecondary,
	},
	row: {
		flexDirection: 'row',
		justifyContent: 'space-between',
		gap: space.md,
		marginTop: space.xs,
	},
	warning: {
		alignSelf: 'flex-start',
		marginTop: space.sm,
	},
});

export default StocktakeListItem;
